import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * UIPreferencesApi
 * Manages per-user UI preference JSON blob stored server-side.
 * Endpoint base: /api/user-preferences/{userId}
 * 
 * JSON Schema (version 2 - current):
 * {
 *   "version": 2,
 *   "theme": string|null,               // theme key (matches ThemeContext keys)
 *   "language": string|null,            // i18n locale code
 *   "density": "comfortable" | "compact" | null,
 *   "experiments": { [flag: string]: boolean }, // feature flag toggles
 *   "gamification": {                   // NEW in V2: gamification tracking
 *     "lastLoginDate": string|null,     // ISO date of last login
 *     "currentStreak": number,          // consecutive days streak
 *     "longestStreak": number,          // all-time best streak
 *     "totalLogins": number,            // lifetime login count
 *     "streakStartDate": string|null,   // when current streak began
 *     "achievements": string[]          // unlocked achievement IDs
 *   },
 *   "has_seen_plan_picker": boolean,    // Whether user has seen first-login plan picker
 *   "updatedAt": ISOString,             // last update timestamp
 *   "migratedAt": ISOString|undefined  // when auto-migration occurred (if applicable)
 * }
 * 
 * Version History:
 * - V1: Initial schema (theme, language, density, experiments)
 * - V2: Added gamification tracking (auto-migrated on client load)
 * 
 * Backward Compatibility:
 * - V1 clients can read V2 data (ignores unknown gamification field)
 * - V2 clients auto-upgrade V1 data on load via preferencesMigration.js
 * - Backend stores JSON blob without validation (schemaless)
 */
export const UIPreferencesApi = {
  get: async (userId) => {
    const resp = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/user-preferences/${userId}`);
    
    // Check status before parsing - throw error with status for 404/other errors
    if (!resp.ok) {
      const error = new Error(`HTTP ${resp.status}`);
      error.status = resp.status;
      error.response = resp;
      throw error;
    }
    
    try {
      if (resp && typeof resp.json === 'function') {
        return await resp.json();
      }
      return resp; // already parsed or unexpected shape
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('UIPreferencesApi.get: failed to parse JSON', e);
      return null;
    }
  },
  exists: async (userId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/user-preferences/${userId}/exists`),
  upsert: async (userId, prefs) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/user-preferences/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs)
  }),
  replace: async (userId, prefs) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/user-preferences/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs)
  }),
  patch: async (userId, partial) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/user-preferences/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(partial)
  }),
  delete: async (userId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/user-preferences/${userId}`, { method: 'DELETE' })
};

export default UIPreferencesApi;
